import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faBed,
  faBroom,
  faCamera,
  faClock,
  faCoffee,
  faComments,
  faDollarSign,
  faKey,
  faLeaf,
  faMapMarkerAlt,
  faParking,
  faSnowflake,
  faUsers,
  faUtensils,
  faVolumeUp,
  faWifi,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useSearchParams } from 'react-router-dom'
import { ReactComponent as TextQuoteIcon } from 'src/assets/svg/text-quote.svg'
import { useReviewAnalysisDateRange } from 'src/hooks/useReviewAnalysisDateRange'
import { useGetReviewAnalysisTopicDetailQuery } from 'src/reactQuery/useGetReviewAnalysisTopicDetailQuery'
import { useGetReviewAnalysisPlatformAnalysisQuery } from 'src/reactQuery/useGetReviewAnalysisPlatformAnalysisQuery'
import { useGetReviewAnalysisTopicsQuery } from 'src/reactQuery/useGetReviewAnalysisTopicsQuery'
import {
  ReviewTopicPolarity,
  ReviewTopicsScopeParams,
} from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './topReviewTopics.module.scss'
import { LocationFilter } from '../../LocationFilter'
import { ScopeOption, ScopeSelect } from '../../ScopeSelect'

type TopicVariant = 'positive' | 'negative'

// Which detail page the widget sits on. It decides what the switcher lists:
// facility level → review platforms (Tripadvisor / Google / …); platform level
// → the venues under that platform (Rooms, Stamba, …).
type WidgetLevel = 'facility' | 'platform'

interface TopicQuote {
  rating: number
  text: string
}

interface TopicDetail {
  quotes: TopicQuote[]
  summary: string
}

interface TopReviewTopicsProps {
  level: WidgetLevel
  variant: TopicVariant
}

const TICK_COUNT = 6

const POLARITY_BY_VARIANT: Record<TopicVariant, ReviewTopicPolarity> = {
  negative: 'NEG',
  positive: 'POS',
}

const VARIANT_META: Record<
  TopicVariant,
  {
    badgeBackground: string
    badgeColor: string
    badgeLabel: string
    barColor: string
    chartBackground: string
    emptyText: string
    iconBackground: string
    legendColor: string
    legendLabel: string
    quoteAccent: string
    quoteBackground: string
    quoteBorder: string
    rowHover: string
    title: string
  }
> = {
  negative: {
    badgeBackground: '#FDEDE8',
    badgeColor: '#D9532B',
    badgeLabel: 'Negative',
    barColor: 'rgba(255, 104, 51, 0.8)',
    chartBackground: '#FFE7E733',
    emptyText:
      'We found some reviews, but there are not enough detailed mentions ' +
      'to identify\nthe most common pain points for this location.',
    iconBackground: '#FDEDE8',
    legendColor: '#FF6833',
    legendLabel: 'Negative Reviews',
    quoteAccent: '#FF6833',
    quoteBackground: '#FF68330D',
    quoteBorder: '#FF683399',
    rowHover: '#FF683314',
    title: 'Top Pain Points',
  },
  positive: {
    badgeBackground: '#E7F7EE',
    badgeColor: '#2E9E63',
    badgeLabel: 'Positive',
    barColor: 'rgba(128, 128, 255, 0.8)',
    chartBackground: '#E7F4FF66',
    emptyText:
      'We found some reviews, but there are not enough detailed mentions ' +
      'to identify\nthe most common positive topics for this location.',
    iconBackground: '#EEF1FE',
    legendColor: '#8080FF',
    legendLabel: 'Positive Reviews',
    quoteAccent: '#0AB270',
    quoteBackground: '#0AB2700D',
    quoteBorder: '#0AB27066',
    rowHover: '#8080FF14',
    title: 'Top Positive Topics',
  },
}

const SUBTITLE =
  'Select a category to view a deeper analysis, AI summary, and related ' +
  'customer feedback'

const getNiceStep = (maxValue: number, steps: number) => {
  if (maxValue <= steps) return 1
  const roughStep = maxValue / steps
  const magnitude = 10 ** Math.floor(Math.log10(roughStep))
  const normalized = roughStep / magnitude
  if (normalized <= 1) return magnitude
  if (normalized <= 2) return 2 * magnitude
  if (normalized <= 5) return 5 * magnitude
  return 10 * magnitude
}

const formatCount = (value: number) => value.toLocaleString('en-US')

const ChevronRightIcon: FC = () => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
    <path
      d="M6 4l4 4-4 4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

// Topic names come from unpredictable review data, so the header icon is chosen
// by matching keywords in the name to a FontAwesome icon, with a neutral
// fallback. Rules are checked in order — put the more specific ones first.
const TOPIC_ICON_RULES: { icon: IconDefinition; keywords: string[] }[] = [
  {
    icon: faClock,
    keywords: ['wait', 'slow', 'time', 'peak', 'check-in', 'checkin', 'speed'],
  },
  { icon: faSnowflake, keywords: ['cold', 'lukewarm', 'temperature', 'warm'] },
  {
    icon: faCoffee,
    keywords: ['coffee', 'beverage', 'drink', 'espresso', 'bar'],
  },
  {
    icon: faUtensils,
    keywords: [
      'food',
      'dish',
      'meal',
      'cuisine',
      'ingredient',
      'flavor',
      'taste',
      'menu',
      'breakfast',
      'restaurant',
      'steak',
      'portion',
    ],
  },
  { icon: faLeaf, keywords: ['vegetarian', 'vegan', 'plant', 'gluten'] },
  {
    icon: faUsers,
    keywords: [
      'staff',
      'service',
      'friendly',
      'welcoming',
      'reception',
      'employee',
      'attitude',
      'desk',
      'host',
      'waiter',
    ],
  },
  {
    icon: faDollarSign,
    keywords: [
      'price',
      'value',
      'cost',
      'expensive',
      'overpriced',
      'money',
      'charge',
      'billing',
      'payout',
    ],
  },
  {
    icon: faBroom,
    keywords: ['clean', 'maintenance', 'dirty', 'hygiene', 'tidy', 'dust'],
  },
  {
    icon: faVolumeUp,
    keywords: ['noise', 'noisy', 'loud', 'soundproof', 'quiet'],
  },
  {
    icon: faParking,
    keywords: [
      'parking',
      'accessibility',
      'access',
      'location',
      'central',
      'area',
    ],
  },
  { icon: faCamera, keywords: ['photo', 'picture', 'image', 'look'] },
  { icon: faBed, keywords: ['room', 'bed', 'comfortable', 'sleep', 'linen'] },
  { icon: faWifi, keywords: ['wifi', 'internet', 'connection'] },
  { icon: faKey, keywords: ['key', 'security', 'theft', 'safe', 'lock'] },
  { icon: faMapMarkerAlt, keywords: ['neighborhood', 'district', 'transport'] },
]

const pickTopicIcon = (name: string): IconDefinition => {
  const lower = name.toLowerCase()
  const match = TOPIC_ICON_RULES.find((rule) =>
    rule.keywords.some((keyword) => lower.includes(keyword))
  )
  return match ? match.icon : faComments
}

const SparkleIcon: FC = () => (
  <svg fill="none" height="18" viewBox="0 0 24 24" width="18">
    <path
      d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8L12 3zM18 15l.9 2.1L21 18l-2.1.9L18 21l-.9-2.1L15 18l2.1-.9L18 15z"
      fill="currentColor"
    />
  </svg>
)

// Empty-state glyph: a message bubble with a star punched out of it (evenodd
// so the star is a true hole and reads against the tinted icon box).
const ReviewBubbleIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      clipRule="evenodd"
      d={
        'M9.333 6.667H22.667A2.667 2.667 0 0 1 25.333 9.333V18.667A2.667 ' +
        '2.667 0 0 1 22.667 21.333H14L9.6 24.933A.667.667 0 0 1 8.533 ' +
        '24.4V21.2A2.667 2.667 0 0 1 6.667 18.667V9.333A2.667 2.667 0 0 1 ' +
        '9.333 6.667ZM16 9.867 14.8 12.3 12.113 12.69 14.057 14.585 13.598 ' +
        '17.26 16 15.997 18.402 17.26 17.943 14.585 19.887 12.69 17.2 12.3Z'
      }
      fill="currentColor"
      fillRule="evenodd"
    />
  </svg>
)

const StarIcon: FC<{ filled: boolean }> = ({ filled }) => (
  <svg fill="none" height="14" viewBox="0 0 24 24" width="14">
    <path
      d="M12 3l2.6 5.3 5.9.9-4.2 4.1 1 5.8L12 16.9 6.7 19.2l1-5.8L3.5 9.2l5.9-.9L12 3z"
      fill={filled ? '#F5A623' : '#E1E4EC'}
    />
  </svg>
)

const StarRating: FC<{ rating: number }> = ({ rating }) => (
  <div className={styles.stars}>
    {[1, 2, 3, 4, 5].map((n) => (
      <StarIcon filled={n <= rating} key={n} />
    ))}
  </div>
)

// Shows the full topic name in a custom tooltip when the label is truncated.
const TruncatedLabel: FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null)

  const handleEnter = () => {
    const el = ref.current
    if (el && el.scrollWidth > el.clientWidth) {
      const rect = el.getBoundingClientRect()
      setTip({ x: rect.left + rect.width / 2, y: rect.top })
    }
  }

  return (
    <span
      className={styles.topicLabelText}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setTip(null)}
      ref={ref}
    >
      {text}
      {tip &&
        createPortal(
          <span className={styles.tooltip} style={{ left: tip.x, top: tip.y }}>
            {text}
          </span>,
          document.body
        )}
    </span>
  )
}

interface DetailPanelProps {
  detail: TopicDetail
  onClose: () => void
  title: string
  variant: TopicVariant
}

const DetailPanel: FC<DetailPanelProps> = ({
  detail,
  onClose,
  title,
  variant,
}) => {
  const meta = VARIANT_META[variant]
  return (
    <div
      className={styles.detailPanel}
      style={
        {
          '--quote-bg': meta.quoteBackground,
          '--quote-border': meta.quoteBorder,
        } as React.CSSProperties
      }
    >
      <div className={styles.detailInner}>
        <div className={styles.detailHeader}>
          <button
            aria-label="Close details"
            className={styles.detailClose}
            onClick={onClose}
            type="button"
          >
            <ChevronRightIcon />
          </button>
          <span
            className={styles.detailIcon}
            style={{ background: meta.iconBackground, color: meta.legendColor }}
          >
            <FontAwesomeIcon icon={pickTopicIcon(title)} />
          </span>
          <span className={styles.detailTitle}>{title}</span>
          <span
            className={styles.detailBadge}
            style={{
              background: meta.badgeBackground,
              color: meta.badgeColor,
            }}
          >
            {meta.badgeLabel}
          </span>
        </div>

        <div className={styles.detailSection}>
          <div className={styles.detailSectionTitle}>
            <span className={styles.detailSectionIcon}>
              <SparkleIcon />
            </span>
            AI Summary
          </div>
          <p className={styles.detailSummary}>{detail.summary}</p>
        </div>

        <div className={styles.detailSectionQuotes}>
          <div className={styles.detailSectionTitle}>
            <span
              className={styles.detailSectionIcon}
              style={{ color: meta.quoteAccent }}
            >
              <TextQuoteIcon />
            </span>
            Top Quotes
          </div>
          <div className={styles.quotesList}>
            {detail.quotes.map((quote) => (
              <div className={styles.quoteCard} key={quote.text}>
                <StarRating rating={quote.rating} />
                <p className={styles.quoteText}>{quote.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export const TopReviewTopics: FC<TopReviewTopicsProps> = ({
  level,
  variant,
}) => {
  // Rendered inside the facility/platform detail routes, so the relevant ids
  // are on the URL — the parents don't need to thread them through.
  const {
    campaignId = '',
    facilityId = '',
    platformId = '',
  } = useParams<{
    campaignId: string
    facilityId?: string
    platformId?: string
  }>()
  const range = useReviewAnalysisDateRange()
  // Facility page: the page-level platform tab (?platform) and location filter
  // (?venue) scope every widget on the page, this one included.
  const [searchParams] = useSearchParams()
  const pagePlatform =
    level === 'facility' ? searchParams.get('platform') || undefined : undefined
  const pageVenue =
    level === 'facility' ? searchParams.get('venue') || undefined : undefined
  // The widget's own filter, as in the design: a location on the facility
  // page ("All Locations"), facilities on the platform page ("All
  // Facilities"). They replaced the platform / venue switcher (QA, 2026-10-13).
  const [activeVenue, setActiveVenue] = useState<string | undefined>()
  const [facilityIds, setFacilityIds] = useState<string[]>([])
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null)
  const meta = VARIANT_META[variant]

  // A location picked on the page's header wins, and hides this filter.
  useEffect(() => {
    if (pageVenue) setActiveVenue(undefined)
  }, [pageVenue])
  useEffect(() => {
    setFacilityIds([])
  }, [platformId])

  const scope: ReviewTopicsScopeParams =
    level === 'facility'
      ? {
          facilityId,
          platform: pagePlatform,
          range,
          venueName: pageVenue ?? activeVenue,
        }
      : { facilityIds, platform: platformId, range }

  // The platform page's facilities, from the read the page already makes.
  const { data: platformAnalysis } = useGetReviewAnalysisPlatformAnalysisQuery(
    level === 'platform' ? campaignId : '',
    platformId,
    undefined,
    range
  )
  const facilityOptions: ScopeOption[] = (platformAnalysis?.facilityTabs ?? [])
    .filter((tab) => !!tab.id)
    .map((tab) => ({ id: tab.id as string, name: tab.name }))

  const { data, isPending } = useGetReviewAnalysisTopicsQuery(
    campaignId,
    POLARITY_BY_VARIANT[variant],
    scope
  )
  // Lazy: disabled (name '') until a bar is clicked.
  const { data: detailData, isPending: isDetailPending } =
    useGetReviewAnalysisTopicDetailQuery(campaignId, selectedTopic ?? '', scope)

  const venues = data?.tabs.venues ?? []
  // A date-filter change can remove the picked location: back to All.
  useEffect(() => {
    if (activeVenue && data && !venues.includes(activeVenue)) {
      setActiveVenue(undefined)
      setSelectedTopic(null)
    }
  }, [activeVenue, data, venues])

  // The open topic may not exist once the page's platform/venue changes.
  useEffect(() => {
    setSelectedTopic(null)
  }, [pagePlatform, pageVenue, activeVenue, facilityIds])

  const entries = useMemo(
    () => [...(data?.topics ?? [])].sort((a, b) => b.count - a.count),
    [data]
  )

  const { ticks, xMax } = useMemo(() => {
    const maxCount = entries.reduce((max, e) => Math.max(max, e.count), 0)
    const step = getNiceStep(Math.max(maxCount, 1), TICK_COUNT)
    const upper = Math.max(step * TICK_COUNT, step)
    return {
      ticks: Array.from({ length: TICK_COUNT + 1 }, (_, i) => i * step),
      xMax: upper,
    }
  }, [entries])

  // Widget-level empty state: the unscoped (All) view resolved with no tagged
  // reviews at all — there is nothing to chart on any tab. The platform
  // switcher and subtitle are hidden; there is nothing to switch between yet.
  const isFiltered = !!activeVenue || facilityIds.length > 0
  const hasData = isPending || isFiltered || entries.length > 0

  if (!hasData) {
    return (
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h3 className={`${styles.title} ${styles.titleEmpty}`}>
              {meta.title}
            </h3>
          </div>
        </div>
        <div className={styles.emptyState}>
          <span className={styles.emptyStateIconBox}>
            <ReviewBubbleIcon className={styles.emptyStateIcon} />
          </span>
          <h3 className={styles.emptyStateTitle}>Not Enough Review Data Yet</h3>
          <p className={styles.emptyStateText}>{meta.emptyText}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>{meta.title}</h3>
          <p className={styles.subtitle}>{SUBTITLE}</p>
        </div>
        {level === 'facility' && !pageVenue && venues.length > 1 && (
          <LocationFilter
            locations={venues}
            onChange={setActiveVenue}
            value={activeVenue}
          />
        )}
        {level === 'platform' && facilityOptions.length > 1 && (
          <ScopeSelect
            allLabel="All Facilities"
            noun="facilities"
            onChange={setFacilityIds}
            options={facilityOptions}
            selected={facilityIds}
          />
        )}
      </div>

      <div className={styles.body}>
        <div
          className={styles.chart}
          style={
            {
              '--row-hover': meta.rowHover,
              background: meta.chartBackground,
            } as React.CSSProperties
          }
        >
          <div className={styles.chartHeader}>
            <div className={styles.axisSpacer} />
            <div className={styles.axisTicks}>
              {/* Drop the final tick so the right-most label isn't clipped. */}
              {ticks.slice(0, -1).map((tick, i) => (
                <span
                  className={styles.axisTick}
                  key={tick}
                  style={{ left: `${(i / TICK_COUNT) * 100}%` }}
                >
                  {tick}
                </span>
              ))}
            </div>
          </div>

          {entries.length === 0 ? (
            <div className={styles.empty}>
              {isPending ? 'Loading…' : 'No data'}
            </div>
          ) : (
            <div className={styles.chartBody}>
              {/* Continuous grid behind the rows: a solid zero axis line plus a
                  dashed guide at each tick, spanning the full body height. */}
              <div className={styles.gridOverlay}>
                <span className={styles.axisLine} />
                {ticks.slice(1).map((tick, i) => (
                  <span
                    className={styles.gridLine}
                    key={tick}
                    style={{ left: `${((i + 1) / TICK_COUNT) * 100}%` }}
                  />
                ))}
              </div>
              {entries.map((entry) => (
                <button
                  className={`${styles.row} ${
                    selectedTopic === entry.name ? styles.rowSelected : ''
                  }`}
                  key={entry.name}
                  onClick={() => setSelectedTopic(entry.name)}
                  type="button"
                >
                  <div className={styles.topicLabel}>
                    <TruncatedLabel text={entry.name} />
                  </div>
                  <div className={styles.plotArea}>
                    <div className={styles.track}>
                      <div
                        className={styles.bar}
                        style={{
                          background: meta.barColor,
                          width: `${(entry.count / xMax) * 100}%`,
                        }}
                      >
                        <span className={styles.barValue}>
                          {formatCount(entry.count)}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className={styles.legend}>
            <span
              className={styles.legendDot}
              style={{ background: meta.legendColor }}
            />
            <span className={styles.legendLabel}>{meta.legendLabel}</span>
          </div>
        </div>

        {selectedTopic && (
          <DetailPanel
            detail={{
              quotes: (detailData?.quotes ?? []).map((quote) => ({
                rating: quote.rating,
                text: `"${quote.text}"`,
              })),
              summary:
                detailData?.summary ??
                (isDetailPending
                  ? 'Loading AI summary…'
                  : 'No AI summary is available for this topic yet.'),
            }}
            onClose={() => setSelectedTopic(null)}
            title={selectedTopic}
            variant={variant}
          />
        )}
      </div>
    </div>
  )
}
